// src/StepUpSIPCalculator.js
import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSchema from '../../FAQSchema';

// Register necessary components
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend
);

const StepUpSIPCalculator = () => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(5000);
  const [annualStepUp, setAnnualStepUp] = useState(10); // Step-up percentage
  const [expectedReturnRate, setExpectedReturnRate] = useState(12); // Expected return rate (per annum)
  const [timePeriod, setTimePeriod] = useState(10); // Time period in years
  const [investedAmount, setInvestedAmount] = useState(0);
  const [estimatedReturns, setEstimatedReturns] = useState(0);
  const [totalValue, setTotalValue] = useState(0);

  const calculateSIP = () => {
    let totalInvested = 0;
    let futureValue = 0;
    const monthlyRate = expectedReturnRate / 12 / 100;
    let currentMonthlyInvestment = monthlyInvestment;

    for (let year = 1; year <= timePeriod; year++) {
      for (let month = 1; month <= 12; month++) {
        totalInvested += currentMonthlyInvestment;
        futureValue += currentMonthlyInvestment * Math.pow((1 + monthlyRate), (timePeriod * 12 - ((year - 1) * 12 + month)));
      }
      currentMonthlyInvestment += (currentMonthlyInvestment * annualStepUp / 100);
    }

    const totalEstReturns = futureValue - totalInvested;

    setInvestedAmount(totalInvested.toFixed(2));
    setEstimatedReturns(totalEstReturns.toFixed(2));
    setTotalValue(futureValue.toFixed(2));
  };

  const handleMonthlyInvestmentChange = (e) => {
    setMonthlyInvestment(parseInt(e.target.value));
  };

  const handleAnnualStepUpChange = (e) => {
    setAnnualStepUp(parseFloat(e.target.value));
  };

  const handleExpectedReturnRateChange = (e) => {
    setExpectedReturnRate(parseFloat(e.target.value));
  };

  const handleTimePeriodChange = (e) => {
    setTimePeriod(parseInt(e.target.value));
  };

  const data = {
    labels: ['Invested Amount', 'Estimated Returns'],
    datasets: [
      {
        label: 'SIP Breakdown',
        data: [investedAmount, estimatedReturns],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };
function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
      {
        question: 'What is a Step-Up SIP?',
        answer: 'A Step-Up SIP is a type of Systematic Investment Plan where your monthly investment amount increases every year by a fixed percentage.',
      },
      {
        question: 'Why should I choose a Step-Up SIP over a regular SIP?',
        answer: 'It allows your investments to grow with your income, helps beat inflation, and accelerates wealth creation compared to a flat SIP.',
      },
      {
        question: 'Can I enter a custom SIP increase amount instead of a percentage?',
        answer: 'The calculator uses a percentage-based step-up. However, you can try different percentages to simulate a fixed amount increase.',
      },
      {
        question: 'What is the ideal percentage to step up my SIP?',
        answer: 'A 10–15% annual increase is generally recommended, especially if your salary grows at a similar rate.',
      },
      {
        question: 'How is the maturity value calculated?',
        answer: 'The calculator uses compound interest principles, adjusting the SIP amount each year based on your step-up percentage.',
      },
      {
        question: 'Can I calculate for multiple SIPs together?',
        answer: 'You can use the calculator separately for each SIP or combine results manually to understand total projections.',
      },
      {
        question: 'Does the calculator consider market volatility?',
        answer: 'No, it uses a fixed expected return rate. Market returns vary, so treat the result as an estimate.',
      },
      {
        question: 'What happens if I skip a SIP or stop increasing it?',
        answer: 'Your actual corpus will be lower than the projection. Use the calculator to simulate such changes.',
      },
      {
        question: 'Can I change the SIP frequency (e.g., quarterly)?',
        answer: 'Currently, the calculator works with monthly SIPs only, which is the most common frequency.',
      },
      {
        question: 'Are the returns guaranteed?',
        answer: 'No. Mutual funds are market-linked instruments. The calculator gives estimated returns based on average rates.',
      },
      {
        question: 'Is there a mobile version of this calculator?',
        answer: 'Yes, the calculator is responsive and works seamlessly on smartphones and tablets.',
      },
      {
        question: 'Does this help in goal-based planning?',
        answer: 'Absolutely. You can calculate how much to invest and step up annually to meet your goals within a desired timeframe.',
      },
      {
        question: 'Is tax included in the return calculation?',
        answer: 'No, the calculator shows pre-tax maturity values. Taxation depends on the fund type and holding period.',
      },
      {
        question: 'Can this be used for ELSS or tax-saving mutual funds?',
        answer: 'Yes, but remember to factor in the 3-year lock-in period for each investment in ELSS funds.',
      },
      {
        question: 'How often should I revisit my SIP plan?',
        answer: 'It’s good practice to review your SIP performance and increase rate annually during income revisions.',
      },
    ];
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
  return (
    <>
    <Helmet>
         <title>Most Credible SIP Calculator – Increase SIP & Estimate Returns</title>
        <meta  name='description' content='Use our step-up SIP calculator to estimate returns when increasing your SIP periodically. Plan your investments smartly.'/>
        <meta name='keywords' content='mutual fund calculator, lumpsum calculator, swp calculator, systematic withdrawal plan calculator, state bank of india sip calculator, systematic investment plan sbi calculator' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/step-up-sip-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Set Up SIP Calculator</span>
        </li> 
      
      </ol>
    </nav>
    <div className='py-4'>
        
    <div className="container mx-auto p-4 shadow-xl rounded-md">
      <h1 className="text-2xl font-bold mb-4">Step Up SIP Calculator</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Monthly Investment</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                value={monthlyInvestment}
                onChange={handleMonthlyInvestmentChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">₹ {monthlyInvestment.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min="0"
              max="100000"
              value={monthlyInvestment}
              onChange={(e) => setMonthlyInvestment(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Annual Step-Up (%)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={annualStepUp}
                onChange={handleAnnualStepUpChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{annualStepUp} %</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="0.1"
              value={annualStepUp}
              onChange={(e) => setAnnualStepUp(parseFloat(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Expected Return Rate (P.A.)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={expectedReturnRate}
                onChange={handleExpectedReturnRateChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{expectedReturnRate} %</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="0.1"
              value={expectedReturnRate}
              onChange={(e) => setExpectedReturnRate(parseFloat(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Time Period (Years)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="30"
                value={timePeriod}
                onChange={handleTimePeriodChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{timePeriod} Yr</span>
            </div>
            <input
              type="range"
              min="0"
              max="30"
              value={timePeriod}
              onChange={(e) => setTimePeriod(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <button
            onClick={calculateSIP}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Calculate
          </button>
        </div>
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-bold">Results</h2>
            <p>Invested Amount: ₹ {parseFloat(investedAmount).toLocaleString()}</p>
            <p>Estimated Returns: ₹ {parseFloat(estimatedReturns).toLocaleString()}</p>
            <p>Total Value (Invested Amount + Returns): ₹ {parseFloat(totalValue).toLocaleString()}</p>
          </div>
          <div>
            <h2 className="text-xl font-bold">Breakdown</h2>
            <div className="flex justify-center">
              <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

    
    <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
    <div className="w-full md:w-2/3 p-4 mx-auto">
    <div className="container mx-auto py-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            What is a Step-Up SIP Calculator?
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            The Step-Up SIP Calculator is an advanced investment tool designed to help users project their future mutual fund returns when they plan to increase their monthly SIP (Systematic Investment Plan) amount annually. Unlike a regular SIP calculator, which assumes a constant investment every month, the step-up calculator incorporates a yearly increment — simulating a more realistic approach where your investments grow in line with your income.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            This calculator is ideal for salaried individuals, freelancers, or business owners who expect their income to increase over time and wish to invest more accordingly. With just a few inputs — including your initial SIP amount, expected rate of return, duration, and percentage increase — this tool gives you an accurate projection of how your wealth could grow over time with a step-up strategy.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Plan for Growth with Realistic Projections
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              One of the most powerful features of the Step-Up SIP Calculator is that it allows you to model future investment growth in a more practical and achievable manner.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Most people start their SIPs with a modest amount and then gradually increase it as their salary or business income rises. A step-up SIP strategy accounts for this natural progression. By using this calculator, you can visualize how small annual increases in your SIP amount can lead to significant wealth accumulation over the long term.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This realistic modeling helps align your investment journey with your career or income growth — encouraging better planning and disciplined wealth creation.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Achieve Financial Goals Faster with a Step-Up Strategy
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              If you have long-term financial goals such as buying a house, building a retirement corpus, funding your child’s education, or achieving financial independence, simply investing a fixed monthly amount may not be enough.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using the Step-Up SIP Calculator, you can:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Understand how increasing your SIP contribution by 5%, 10%, or 15% annually can drastically reduce the time needed to reach your goal.</li>
              <li>Compare multiple scenarios to identify the most efficient path to your target.</li>
              <li>Plan for inflation-adjusted goals by matching your SIP increment with rising costs.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              By optimizing your SIP step-up rate, you not only bridge the gap between goals and savings but also stay motivated as you witness your growing potential over time.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Make Informed Investment Decisions with Scenario Planning
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              One of the key advantages of this calculator is its ability to simulate various investment scenarios. You can experiment with different combinations of:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Initial monthly SIP amount</li>
              <li>Expected annual return (%)</li>
              <li>Investment duration (in years)</li>
              <li>Annual SIP increase percentage</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This helps you understand the trade-offs between time, returns, and contributions. For example, you might discover that increasing your SIP by 10% annually can help you reach your ₹1 crore target 2–3 years earlier than sticking with a flat SIP amount.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Armed with these insights, you’ll be empowered to make data-backed investment decisions that are aligned with your goals, timeline, and risk appetite.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Simple, Intuitive, and Instant Results
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              You don’t need to be a financial expert to use TheFinMaster's Step-Up SIP Calculator. The interface is built with simplicity in mind — enabling both beginners and seasoned investors to:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Enter their data quickly</li>
              <li>View instant results</li>
              <li>Understand key metrics such as total invested amount, projected returns, and maturity value</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              There's no need for manual Excel sheets or confusing formulas — our calculator handles the compounding logic and step-up increase behind the scenes, giving you quick and accurate projections in seconds.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Explore Investment Potential Anytime, Anywhere
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              TheFinMaster’s calculator is completely free to use and is optimized for mobile, desktop, and tablet users. Whether you're exploring options during a lunch break, in a financial planning meeting, or simply browsing at home, the tool is available 24/7.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              You can use it to:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Plan yearly SIP increments during salary appraisals</li>
              <li>Compare multiple return scenarios before choosing a fund</li>
              <li>Align your SIP strategy with evolving life milestones</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              With no sign-ups or payment required, you can try different investment combinations and truly explore the power of growing your SIP over time.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How the Step-Up SIP Calculator Works
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Here’s a breakdown of the simple steps involved:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Inputs You Provide:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Initial SIP Amount: Your starting monthly investment</li>
              <li>Investment Duration: Number of years you plan to invest</li>
              <li>Expected Return Rate: Annual average return (%) based on fund type</li>
              <li>Annual SIP Increase (%): Percentage by which your SIP will grow every year</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>What You Get:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Total Invested Amount</li>
              <li>Estimated Maturity Value</li>
              <li>Total Returns Generated</li>
              <li>Year-wise investment breakdown (optional)</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Let’s say you start with ₹5,000/month, increase your SIP by 10% every year, invest for 20 years, and expect 12% annual returns. With a step-up SIP, your corpus can grow significantly beyond what a fixed SIP of ₹5,000/month would yield. You may end up with a maturity value over ₹90–100 lakhs instead of ₹50–60 lakhs with a flat SIP.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Choose TheFinMaster’s Step-Up SIP Calculator?
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Designed by finance professionals for everyday users</li>
              <li>Real-time projection based on your inputs</li>
              <li>Accurate compounding logic and incremental growth modeling</li>
              <li>No login, no fees, no data collection</li>
              <li>Great for tax planning, retirement planning, and early goal achievement</li>
            </ul>
          </div>
        </div>

     <div className="w-full md:w-1/3 p-4">
       <div className="bg-white shadow-md rounded-lg overflow-hidden">
         <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
           <h2 className="text-lg font-semibold ">Popular Categories</h2>
         </div>
         <div className="p-2 ">
         <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
             {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
           </ul>
         </div>
       </div>

     </div>

   </div>
   <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h1>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </>
  );
};

export default StepUpSIPCalculator;
